import React, { FC, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

// Constants
import { routes } from '@sketch/constants'
import { useFlag } from 'containers/flags'

// Utils
import useUserProfile from 'modules/user/useUserProfile'
import useSignOut from 'modules/user/useSignOut'

// Components
import { useToast } from '@sketch/toasts'
import { Title, Subtitle } from 'components/IndexLayout'

// Styles
import {
  IndexLayout,
  Wrapper,
  TextInbox as Text,
  StyledLinkButton,
  DoThisLaterButton,
} from './CheckYourInbox.styles'

// Types
import { useResendVerificationEmailMutation } from '@sketch/gql-types'

const CheckYourInboxView: FC = () => {
  const history = useHistory()
  const { showToast } = useToast()

  // TODO: Remove usage of mandatory-email-verification when release to production
  // https://github.com/sketch-hq/Cloud/issues/11277
  const isMandatoryEmailVerification = useFlag('mandatory-email-verification')

  const { data } = useUserProfile()

  // Redirect already verified users to the entry route
  useEffect(() => {
    if (!isMandatoryEmailVerification) {
      if (data?.me.isVerified) history.push(routes.ENTRY.create({}))
    }
  }, [data, history, isMandatoryEmailVerification])

  // Operations
  const [resendVerificationEmail] = useResendVerificationEmailMutation({
    redirectErrors: true,
    onCompleted: () => showToast(`An email has been sent to ${data?.me.email}`),
    onError: 'show-toast',
  })

  const signOut = useSignOut({
    location: routes.SIGN_UP.create({
      query: {
        email: encodeURIComponent(data?.me.email ?? ''),
        professionalRole: data?.me.professionalRole ?? '',
      },
    }),
    reason: 'Mistyped email button',
  })

  return (
    <IndexLayout variant="green" maxContainerWidth="595px">
      <Wrapper>
        <Title>Check Your Inbox</Title>
        <Subtitle>
          A verification email is on its way to{' '}
          <strong>{data?.me.email}</strong>.
          <br />
          Confirm your email to start using Sketch.
        </Subtitle>
        <Text>
          <span>
            Can&apos;t find the email?{' '}
            <StyledLinkButton onClick={() => resendVerificationEmail()}>
              Resend
            </StyledLinkButton>{' '}
            the verification email
          </span>
        </Text>
        <Text>
          <span>
            Mistyped your email?&nbsp;
            <StyledLinkButton variant="secondary" onClick={signOut}>
              Sign out
            </StyledLinkButton>
            &nbsp;and try again
          </span>
        </Text>
        {!isMandatoryEmailVerification && (
          <DoThisLaterButton
            to={routes.WORKSPACE_AGNOSTIC_DISCOVER.create({})}
            variant="primary-untinted"
          >
            Do This Later
          </DoThisLaterButton>
        )}
      </Wrapper>
    </IndexLayout>
  )
}

export default CheckYourInboxView
